import type { AoMeta } from '../sim/types';

/**
 * Converts between the simulation's AO (area of operations) centimeter frame and WGS84 lon/lat.
 *
 * The sim keeps every position as integer centimeters from the AO's south-west corner, x east,
 * y north. The AO is small (15 km) so a local equirectangular fit using the meters-per-degree
 * constants baked into the AO metadata is accurate to well under a meter across the box.
 */
export class Projection {
  readonly widthM: number;
  readonly heightM: number;

  constructor(readonly meta: AoMeta) {
    this.widthM = meta.w * meta.cellM;
    this.heightM = meta.h * meta.cellM;
  }

  /** AO centimeters → [lng, lat]. */
  cmToLngLat(x: number, y: number): [number, number] {
    const m = this.meta;
    return [m.originLon + x / 100 / m.mPerDegLon, m.originLat + y / 100 / m.mPerDegLat];
  }

  /** [lng, lat] → AO centimeters (not clamped to the AO; may be negative or past the far edge). */
  lngLatToCm(lng: number, lat: number): { x: number; y: number } {
    const m = this.meta;
    return {
      x: Math.round((lng - m.originLon) * m.mPerDegLon * 100),
      y: Math.round((lat - m.originLat) * m.mPerDegLat * 100),
    };
  }

  /** Meters → centimeters helper so callers never hard-code the factor. */
  static mToCm(meters: number): number {
    return Math.round(meters * 100);
  }

  /** The AO bounding box as [[west, south], [east, north]], optionally padded by a fraction of its size. */
  bounds(padFraction = 0): [[number, number], [number, number]] {
    const padX = this.widthM * 100 * padFraction;
    const padY = this.heightM * 100 * padFraction;
    const sw = this.cmToLngLat(-padX, -padY);
    const ne = this.cmToLngLat(this.widthM * 100 + padX, this.heightM * 100 + padY);
    return [sw, ne];
  }

  /** Centre of the AO as [lng, lat]. */
  center(): [number, number] {
    return this.cmToLngLat((this.widthM * 100) / 2, (this.heightM * 100) / 2);
  }
}
